import React, { useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Card, Button, Typography, Space } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { APP_NAME } from "../helpers/constants";
import { AuthContext } from "../context/AuthProvider";

const { Title, Text, Paragraph } = Typography;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Fire celebratory confetti on mount
  useEffect(() => {
    let isCancelled = false;
    const launch = async () => {
      try {
        const confetti = (await import("canvas-confetti")).default;
        if (isCancelled) return;

        const defaults = {
          disableForReducedMotion: true,
          colors: ["#2C5F2D", "#4A7C59", "#70F2BA", "#F6B438", "#1f2937"],
          scalar: 1,
          ticks: 180,
          zIndex: 1000,
        };

        const burst = (particleRatio, opts) => {
          confetti({
            ...defaults,
            ...opts,
            particleCount: Math.floor(200 * particleRatio),
          });
        };

        // Sequence of bursts for a richer effect
        burst(0.25, { spread: 26, startVelocity: 35, origin: { y: 0.1 } });
        burst(0.2, { spread: 60, origin: { y: 0.1 } });
        burst(0.35, { spread: 100, decay: 0.92, origin: { y: 0.1 } });
        burst(0.1, { spread: 120, startVelocity: 25, origin: { y: 0.1 } });
        setTimeout(() => {
          burst(0.6, { spread: 70, scalar: 0.9, origin: { y: 0.15 } });
        }, 450);
      } catch (e) {
        // If the dependency isn't available, silently skip
      }
    };

    launch();
    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <Container>
      <SuccessCard>
        <Content>
          <IconWrap>
            <CheckCircleOutlined />
          </IconWrap>
          <Heading level={2}>Payment Complete!</Heading>
          <Description>
            Excellent! Your rent payment has been securely processed and
            confirmed. You're all set!
          </Description>

          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            <div>
              <NextLabel strong>What happens next?</NextLabel>
              <Checklist>
                <li>Payment confirmation email sent to your inbox</li>
                <li>Your landlord has been automatically notified</li>
                <li>Your payment history has been updated in real-time</li>
              </Checklist>
            </div>

            <CloseMessage>You can now close this window.</CloseMessage>
          </Space>
        </Content>
      </SuccessCard>
    </Container>
  );
};

export default PaymentSuccess;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const SuccessCard = styled(Card)`
  max-width: 500px;
  width: 100%;
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  background: white;
  padding: 2%;
  .ant-card-body {
    padding: 2.5rem 2.75rem;
  }

  @media (max-width: 480px) {
    border-radius: 0;
    background: transparent;
    border: 0;
    box-shadow: none;
    padding: 0;
    .ant-card-body {
      padding: 0.5rem 0.75rem 0.75rem 0.75rem;
    }
  }
`;

const Content = styled.div`
  text-align: center;
  margin: 0 auto;
  max-width: 560px;
  width: 100%;

  @media (max-width: 480px) {
    max-width: 420px;
  }
`;

const IconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(74, 124, 89, 0.08);
  color: var(--color-primary);
  margin: 0 auto 12px auto;

  .anticon {
    font-size: 40px;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
    .anticon {
      font-size: 32px;
    }
  }
`;

const Heading = styled(Title)`
  && {
    color: #0f172a;
    margin: 0 0 8px 0;
    font-weight: 800;
  }

  @media (max-width: 480px) {
    && {
      font-size: 24px !important;
      line-height: 1.2 !important;
    }
  }
`;

const Description = styled(Paragraph)`
  && {
    font-size: 16px;
    color: #475569;
    margin: 0 0 16px 0;
    line-height: 1.65;
  }

  @media (max-width: 480px) {
    && {
      font-size: 15px;
    }
  }
`;

const NextLabel = styled(Text)`
  && {
    color: #0f172a;
  }
`;

const Checklist = styled.ul`
  text-align: left;
  margin: 12px 0 0 0;
  padding-left: 1.25rem;
  color: #111827;
  line-height: 1.65;
  list-style: disc;
  display: grid;
  row-gap: 8px;

  @media (max-width: 480px) {
    font-size: 15px;
    padding-left: 1rem;
    row-gap: 6px;
  }
`;

const CloseMessage = styled(Paragraph)`
  && {
    font-size: 16px;
    color: #475569;
    margin: 24px 0 0 0;
    line-height: 1.65;
    text-align: center;
    font-weight: 500;
  }

  @media (max-width: 480px) {
    && {
      font-size: 15px;
      margin: 20px 0 0 0;
    }
  }
`;
